import { LitElement, html, unsafeCSS } from 'lit'
import { SVG_ALERT_OUTLINED, SVG_DOWNLOAD } from '@getgo/chameleon-icons'
import { property, state } from 'lit/decorators.js'
import { unsafeSVG } from 'lit/directives/unsafe-svg.js'
import type { GalleryModel, GalleryState, FileModel } from '@/models/gallery.model'
import { GalleryEvent, type PreviewLoadErrorEventDetail } from '@/models/gallery-event'
import type { ViewMode } from '@/services/view-mode.service'
import { fetchGallery } from '@/services/gallery-data.service'
import { downloadFiles, DownloadFetchError } from '@/services/download.service'
import { getViewMode, setViewMode } from '@/services/view-mode.service'
import { GalleryError } from '@/services/gallery-error'
import { getLogger } from '@/logger'
import { GalleryAnalyticsEvent, trackGalleryEvent } from '@/services/gallery-analytics.service'
import { formatFileSize } from '@/utils/format-file-size'
import { t } from '@/i18n/translate-utils'
import styles from './goto-gallery-app.styles.scss?inline'

/**
 * Root orchestration component for the GoTo Gallery SPA.
 *
 * Manages the full application state machine: loading, loaded, error, expired,
 * and not-found states. Composes all child components and owns the download
 * workflow.
 *
 * @attr gallery-id - The gallery ID read from the URL path.
 * @attr preview     - When present, the preview BFF endpoint is used.
 *
 * @fires view-mode-change - Fired by children; handled here to persist the mode.
 * @fires file-open        - Fired by tile/list views to open the detail view.
 * @fires file-select      - Fired by tile/list views to toggle file selection.
 * @fires select-all-toggle - Fired by toolbar to select or deselect all files.
 * @fires detail-close     - Fired by detail view to return to grid/list.
 * @fires detail-prev      - Fired by detail view to navigate to the previous file.
 * @fires detail-next      - Fired by detail view to navigate to the next file.
 * @fires download-request - Fired by the download button to start a download.
 * @fires snackbar-close   - Fired by the error snackbar to dismiss it.
 */
export class GotoGalleryApp extends LitElement {
  static tagName = 'gallery-app'
  static override styles = unsafeCSS(styles)

  /** The gallery ID passed from `main.ts` via the `gallery-id` attribute. */
  @property({ type: String, attribute: 'gallery-id' }) galleryId = ''

  /** When `true`, the preview BFF endpoint is used for data fetching. */
  @property({ type: Boolean, attribute: 'preview' }) preview = false

  /** Current loading/error state of the gallery. */
  @state() state: GalleryState = 'loading'

  /** The loaded gallery data. Null until the fetch resolves successfully. */
  @state() private _gallery: GalleryModel | null = null

  /** Set of selected file IDs. Initialised to all IDs on load. */
  @state() selectedIds: Set<string> = new Set()

  /** Current view mode (tiles or list). Read from localStorage on init. */
  @state() viewMode: ViewMode = 'tiles'

  /** Index of the file currently shown in the detail view. Null when not in detail view. */
  @state() activeIndex: number | null = null

  /** Last index opened in the detail view; used to restore focus when closing. */
  private _prevDetailIndex: number | null = null

  /** True while a ZIP download is in progress. */
  @state() isDownloading = false

  /** True when the last download attempt failed. */
  @state() downloadError = false

  /** Ensures we only auto-refresh share metadata once per loaded share. */
  private _metadataRefreshAttempted = false

  override connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener(GalleryEvent.VIEW_MODE_CHANGE, this._onViewModeChange as EventListener)
    this.addEventListener(GalleryEvent.FILE_OPEN, this._onFileOpen as EventListener)
    this.addEventListener(GalleryEvent.FILE_SELECT, this._onFileSelect as EventListener)
    this.addEventListener(GalleryEvent.SELECT_ALL_TOGGLE, this._onSelectAllToggle)
    this.addEventListener(GalleryEvent.DETAIL_CLOSE, this._onDetailClose)
    this.addEventListener(GalleryEvent.DETAIL_PREV, this._onDetailPrev)
    this.addEventListener(GalleryEvent.DETAIL_NEXT, this._onDetailNext)
    this.addEventListener(GalleryEvent.DOWNLOAD_REQUEST, this._onDownloadRequest)
    this.addEventListener(GalleryEvent.PREVIEW_LOAD_ERROR, this._onPreviewLoadError as EventListener)
    void this._loadGallery()
  }

  override disconnectedCallback(): void {
    this.removeEventListener(GalleryEvent.VIEW_MODE_CHANGE, this._onViewModeChange as EventListener)
    this.removeEventListener(GalleryEvent.FILE_OPEN, this._onFileOpen as EventListener)
    this.removeEventListener(GalleryEvent.FILE_SELECT, this._onFileSelect as EventListener)
    this.removeEventListener(GalleryEvent.SELECT_ALL_TOGGLE, this._onSelectAllToggle)
    this.removeEventListener(GalleryEvent.DETAIL_CLOSE, this._onDetailClose)
    this.removeEventListener(GalleryEvent.DETAIL_PREV, this._onDetailPrev)
    this.removeEventListener(GalleryEvent.DETAIL_NEXT, this._onDetailNext)
    this.removeEventListener(GalleryEvent.DOWNLOAD_REQUEST, this._onDownloadRequest)
    this.removeEventListener(GalleryEvent.PREVIEW_LOAD_ERROR, this._onPreviewLoadError as EventListener)
    super.disconnectedCallback()
  }

  private async _loadGallery(): Promise<void> {
    try {
      const gallery = await this._fetchLatestGallery()
      this._gallery = gallery
      this.selectedIds = new Set(gallery.files.map(f => f.id))
      this.viewMode = getViewMode()
      if (gallery.files.length === 1) {
        this.activeIndex = 0
      }
      this._metadataRefreshAttempted = false
      this.state = 'loaded'
      trackGalleryEvent(GalleryAnalyticsEvent.LINK_OPENED, {
        ...this._getGalleryAnalyticsContext(),
        file_count: gallery.files.length,
        file_types: this._getUniqueFileTypes(gallery.files),
      })
    } catch (err) {
      if (err instanceof GalleryError) {
        this.state = err.reason as GalleryState
        trackGalleryEvent(GalleryAnalyticsEvent.LINK_OPEN_FAILED, {
          ...this._getGalleryAnalyticsContext(),
          failure_reason: err.reason,
        })
      } else {
        getLogger().error('[goto-gallery] Failed to load gallery', err)
        this.state = 'error'
        trackGalleryEvent(GalleryAnalyticsEvent.LINK_OPEN_FAILED, {
          ...this._getGalleryAnalyticsContext(),
          failure_reason: 'error',
        })
      }
    }
  }

  private readonly _onViewModeChange = (e: CustomEvent<ViewMode>): void => {
    this.viewMode = e.detail
    setViewMode(e.detail)
    trackGalleryEvent(GalleryAnalyticsEvent.VIEW_MODE_CHANGED, {
      ...this._getGalleryAnalyticsContext(),
      view_mode: e.detail,
    })
  }

  private readonly _onFileOpen = (e: CustomEvent<number>): void => {
    this._prevDetailIndex = e.detail
    this.activeIndex = e.detail

    const openedFile = this._gallery?.files[e.detail]
    if (openedFile) {
      trackGalleryEvent(GalleryAnalyticsEvent.FILE_OPENED, {
        ...this._getGalleryAnalyticsContext(),
        file_id: openedFile.id,
        file_type: openedFile.contentType,
        file_size_bytes: openedFile.size,
        file_index: e.detail,
      })
    }
  }

  private _fetchLatestGallery(): Promise<GalleryModel> {
    return fetchGallery(this.galleryId, { preview: this.preview })
  }

  private readonly _onFileSelect = (e: CustomEvent<string>): void => {
    const next = new Set(this.selectedIds)
    if (next.has(e.detail)) {
      next.delete(e.detail)
    } else {
      next.add(e.detail)
    }
    this.selectedIds = next
  }

  private readonly _onSelectAllToggle = (): void => {
    const allSelected = this._gallery !== null && this.selectedIds.size === this._gallery.files.length
    if (allSelected) {
      this.selectedIds = new Set()
    } else {
      this.selectedIds = new Set(this._gallery?.files.map(f => f.id) ?? [])
    }
  }

  private readonly _onDetailClose = (): void => {
    const prevIndex = this._prevDetailIndex
    this.activeIndex = null
    if (prevIndex !== null) {
      void this.updateComplete.then(() => {
        const root = (this as unknown as { renderRoot?: ParentNode }).renderRoot
        const view = root?.querySelector?.('gallery-tiles-view') ?? root?.querySelector?.('gallery-list-view')
        ;(view as { setActiveIndex?: (n: number) => void } | null)?.setActiveIndex?.(prevIndex)
      })
    }
  }

  private readonly _onDetailPrev = (): void => {
    if (this.activeIndex !== null && this.activeIndex > 0) {
      this.activeIndex -= 1
    }
  }

  private readonly _onDetailNext = (): void => {
    if (this.activeIndex !== null && this._gallery !== null && this.activeIndex < this._gallery.files.length - 1) {
      this.activeIndex += 1
    }
  }

  private readonly _onDownloadRequest = (): void => {
    const hasSelection = this.selectedIds.size > 0
    if (!hasSelection || this.isDownloading) {
      return
    }

    void this._handleDownload()
  }

  private readonly _onPreviewLoadError = (e: CustomEvent<PreviewLoadErrorEventDetail>): void => {
    if (!this._gallery || this.activeIndex === null) {
      return
    }

    const activeFile = this._gallery.files[this.activeIndex]
    if (!activeFile || activeFile.id !== e.detail.id) {
      return
    }

    void this._refreshShareMetadataOnce('preview')
  }

  private readonly _onBannerClose = (): void => {
    this.downloadError = false
  }

  private async _handleDownload(): Promise<void> {
    if (!this._gallery) {
      return
    }

    const selectedFiles = this._gallery.files.filter(f => this.selectedIds.has(f.id))
    const selectedMetrics = this._getSelectedFilesMetrics(selectedFiles)

    trackGalleryEvent(GalleryAnalyticsEvent.DOWNLOAD_REQUESTED, {
      ...this._getGalleryAnalyticsContext(),
      ...selectedMetrics,
      total_file_count: this._gallery.files.length,
    })

    this.isDownloading = true
    try {
      await downloadFiles(selectedFiles, this.galleryId)
      this.downloadError = false
      trackGalleryEvent(GalleryAnalyticsEvent.DOWNLOAD_SUCCEEDED, {
        ...this._getGalleryAnalyticsContext(),
        ...selectedMetrics,
      })
    } catch (err) {
      const shouldRetry = this._isStatusError(err, 403)
      if (shouldRetry && (await this._refreshShareMetadataOnce('download')) && this._gallery) {
        const refreshedSelection = this._gallery.files.filter(f => this.selectedIds.has(f.id))
        try {
          await downloadFiles(refreshedSelection, this.galleryId)
          this.downloadError = false
          trackGalleryEvent(GalleryAnalyticsEvent.DOWNLOAD_SUCCEEDED, {
            ...this._getGalleryAnalyticsContext(),
            ...this._getSelectedFilesMetrics(refreshedSelection),
          })
          return
        } catch (retryErr) {
          getLogger().error('[goto-gallery] Download retry failed', retryErr)
        }
      }

      getLogger().error('[goto-gallery] Download failed', err)
      this.downloadError = true
      trackGalleryEvent(GalleryAnalyticsEvent.DOWNLOAD_FAILED, {
        ...this._getGalleryAnalyticsContext(),
        ...selectedMetrics,
      })
    } finally {
      this.isDownloading = false
    }
  }

  private _getGalleryAnalyticsContext(): { gallery_id: string; is_preview: boolean } {
    return {
      gallery_id: this.galleryId,
      is_preview: this.preview,
    }
  }

  private _getSelectedFilesMetrics(files: FileModel[]): {
    selected_file_count: number
    selected_size_bytes: number
    selected_file_types: string[]
  } {
    return {
      selected_file_count: files.length,
      selected_size_bytes: files.reduce((sum, file) => sum + file.size, 0),
      selected_file_types: this._getUniqueFileTypes(files),
    }
  }

  private _getUniqueFileTypes(files: FileModel[]): string[] {
    return Array.from(new Set(files.map(file => file.contentType || 'unknown'))).sort()
  }

  private _fileIdentityKey(file: FileModel): string {
    return `${file.name}::${file.size}::${file.contentType}`
  }

  private _captureRefreshContext(gallery: GalleryModel): {
    selectedKeys: Set<string>
    activeKey: string | null
  } {
    const selectedKeys = new Set(
      gallery.files.filter(file => this.selectedIds.has(file.id)).map(file => this._fileIdentityKey(file)),
    )

    const activeKey =
      this.activeIndex !== null && gallery.files[this.activeIndex]
        ? this._fileIdentityKey(gallery.files[this.activeIndex])
        : null

    return { selectedKeys, activeKey }
  }

  private _applyRefreshedGallery(gallery: GalleryModel, selectedKeys: Set<string>, activeKey: string | null): void {
    this._gallery = gallery

    const refreshedSelectedIds = gallery.files
      .filter(file => selectedKeys.has(this._fileIdentityKey(file)))
      .map(file => file.id)
    this.selectedIds = new Set(refreshedSelectedIds)

    if (this.activeIndex === null) {
      return
    }

    const nextActiveIndex =
      activeKey !== null ? gallery.files.findIndex(file => this._fileIdentityKey(file) === activeKey) : -1
    this.activeIndex = nextActiveIndex >= 0 ? nextActiveIndex : gallery.files.length > 0 ? 0 : null
  }

  private _isStatusError(err: unknown, status: number): boolean {
    if (err instanceof DownloadFetchError) {
      return err.status === status
    }
    if (err instanceof Error) {
      return err.message.includes(`(${status})`) || err.message.includes(` ${status}`)
    }
    return false
  }

  private async _refreshShareMetadataOnce(reason: 'preview' | 'download'): Promise<boolean> {
    if (!this._gallery || this._metadataRefreshAttempted) {
      return false
    }

    this._metadataRefreshAttempted = true
    const previousGallery = this._gallery
    const { selectedKeys, activeKey } = this._captureRefreshContext(previousGallery)

    try {
      const refreshed = await this._fetchLatestGallery()
      this._applyRefreshedGallery(refreshed, selectedKeys, activeKey)

      getLogger().info('[goto-gallery] Share metadata refreshed after signed URL failure', { reason })
      return true
    } catch (refreshErr) {
      getLogger().error('[goto-gallery] Failed to refresh share metadata', { reason, refreshErr })
      return false
    }
  }

  private _renderErrorBanner() {
    return html`<chameleon-banner variant="danger" centered closable @close=${this._onBannerClose}>
      <chameleon-svg slot="icon">${unsafeSVG(SVG_ALERT_OUTLINED)}</chameleon-svg>
      ${t('gallery.error.download', 'Download failed. Please reload the page and try again.')}
    </chameleon-banner>`
  }

  private _renderDownloadBar() {
    const files = this._gallery?.files ?? []
    const selectedFiles = files.filter((f: FileModel) => this.selectedIds.has(f.id))
    const count = selectedFiles.length
    const label = t('gallery.actions.download', 'Download files ({{count}})', { count })
    const disabled = count === 0 || this.isDownloading

    return html`
      <div class="bottom-bar">
        <chameleon-button
          variant="brand-primary"
          size="large"
          ?is-loading=${this.isDownloading}
          ?disabled=${disabled}
          @click=${this._onDownloadRequest}
        >
          <chameleon-svg slot="start">${unsafeSVG(SVG_DOWNLOAD)}</chameleon-svg>
          ${label}
        </chameleon-button>
      </div>
    `
  }

  override render() {
    if (this.state === 'loading') {
      return html`<gallery-skeleton></gallery-skeleton>`
    }

    if (this.state !== 'loaded' || !this._gallery) {
      return html`<gallery-error reason=${this.state}></gallery-error>`
    }

    if (this.activeIndex !== null) {
      const activeFile = this._gallery.files[this.activeIndex]
      const selectedFiles = this._gallery.files.filter((f: FileModel) => this.selectedIds.has(f.id))
      const selectedSize = formatFileSize(selectedFiles.reduce((sum: number, f: FileModel) => sum + f.size, 0))
      return html`
        ${this.downloadError ? this._renderErrorBanner() : ''}
        <gallery-details-view
          .file=${activeFile}
          .index=${this.activeIndex}
          .total=${this._gallery.files.length}
          .isSelected=${this.selectedIds.has(activeFile?.id ?? '')}
          .selectedCount=${this.selectedIds.size}
          .selectedSize=${selectedSize}
          .isDownloading=${this.isDownloading}
        ></gallery-details-view>
      `
    }

    return html`
      ${this.downloadError ? this._renderErrorBanner() : ''}
      <div class="app-container">
        <div class="gallery-frame">
          <gallery-header
            .title=${this._gallery.title}
            .senderName=${this._gallery.senderName}
            .expiresAt=${this._gallery.expiresAt}
          ></gallery-header>

          <gallery-toolbar
            .selectedCount=${this.selectedIds.size}
            .totalFiles=${this._gallery.files.length}
            .totalSize=${formatFileSize(
              this._gallery.files.filter(f => this.selectedIds.has(f.id)).reduce((sum, f) => sum + f.size, 0),
            )}
            .viewMode=${this.viewMode}
          ></gallery-toolbar>

          <div class="gallery-content">
            ${this.viewMode === 'tiles'
              ? html`<gallery-tiles-view
                  .files=${this._gallery.files}
                  .selectedIds=${this.selectedIds}
                ></gallery-tiles-view>`
              : html`<gallery-list-view
                  .files=${this._gallery.files}
                  .selectedIds=${this.selectedIds}
                ></gallery-list-view>`}
          </div>
        </div>
        ${this._renderDownloadBar()}
      </div>
    `
  }
}
